import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Brain, Heart, Target, Zap, Shield, Award, AlertTriangle,
  Loader2, ExternalLink, BarChart3
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const BATTERY_ICONS = {
  ocean: Brain, eq: Heart, disc: Target, strengths: Zap,
  hogan: Shield, kfla: Award, stallers: AlertTriangle,
};
const BATTERY_COLORS = {
  ocean: '#3B82F6', eq: '#EC4899', disc: '#F5841F', strengths: '#16A34A',
  hogan: '#DC2626', kfla: '#8B5CF6', stallers: '#EAB308',
};
const BATTERY_NAMES = {
  ocean: 'Big Five (OCEAN)', eq: 'Emotional Intelligence', disc: 'DISC Behavioral',
  strengths: 'Strengths Orientation', hogan: 'Hogan Dark Side',
  kfla: 'Korn Ferry Competency', stallers: 'Career Stallers',
};

export default function PublicSharedAssessmentPage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/kairera/assessments/public/${shareId}`);
        if (!res.ok) throw new Error('not_found');
        const json = await res.json();
        setData(json);
      } catch {
        setError('not_found');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0D9488' }} />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
      <div className="text-center">
        <Brain className="w-12 h-12 mx-auto mb-4" style={{ color: '#4B5563' }} />
        <h2 className="text-xl font-bold mb-2" style={{ color: '#F9FAFB' }}>Link Not Found</h2>
        <p className="text-sm" style={{ color: '#6B7280' }}>This shared assessment link is invalid or has expired.</p>
        <a href="/" className="inline-flex items-center gap-1.5 mt-6 px-5 py-2.5 rounded-xl text-xs font-bold"
          style={{ background: '#0D9488', color: '#fff' }}>
          Visit Kairera <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );

  const { user_name, created_at, results } = data;
  const resultMap = {};
  results.forEach(r => { resultMap[r.battery_id] = r; });

  // Build radar data
  const radarData = [];
  if (resultMap.ocean) {
    Object.entries(resultMap.ocean.scores || {}).forEach(([k, v]) => {
      if (typeof v === 'number') radarData.push({ dim: k.slice(0, 8), score: Math.min(v, 10) });
    });
  }
  if (resultMap.eq?.scores?.overall) radarData.push({ dim: 'EQ', score: Math.min(resultMap.eq.scores.overall, 10) });
  if (resultMap.kfla?.scores?.overall) radarData.push({ dim: 'KFLA', score: Math.min(resultMap.kfla.scores.overall, 10) });

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/kairera-mono.png" alt="K" style={{ width: 32, height: 32 }} />
            <img src="/kairera-logo.png" alt="Kairera" style={{ height: 18 }} />
          </div>
          <a href="/" className="text-xs font-semibold px-4 py-2 rounded-lg"
            style={{ background: '#0D9488', color: '#fff' }}
            data-testid="public-cta-btn">
            Get Your Assessment
          </a>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="text-center" data-testid="public-hero">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-2" style={{ color: '#0D9488' }}>
            Career Intelligence Assessment Profile
          </p>
          <h1 className="text-3xl font-black mb-1" style={{ color: '#F9FAFB' }}>{user_name}</h1>
          {created_at && (
            <p className="text-xs" style={{ color: '#6B7280' }}>
              Assessed on {new Date(created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </motion.div>

        {/* Stats */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="public-stats">
          {results.map((r, i) => {
            const color = BATTERY_COLORS[r.battery_id] || '#0D9488';
            const name = BATTERY_NAMES[r.battery_id] || r.battery_id;
            const s = r.scores || {};
            let scoreLabel = '';
            if (s.overall != null) scoreLabel = `${Math.min(s.overall, 10).toFixed(1)}`;
            else if (s.primary) scoreLabel = s.primary.charAt(0).toUpperCase() + s.primary.slice(1);
            else if (s.inverted_score != null) scoreLabel = `${Math.min(s.inverted_score, 10).toFixed(1)}`;
            else if (s.risk_level) scoreLabel = s.risk_level.charAt(0).toUpperCase() + s.risk_level.slice(1);
            else {
              const vals = Object.values(s).filter(v => typeof v === 'number');
              if (vals.length) scoreLabel = `${Math.min(vals.reduce((a, b) => a + b, 0) / vals.length, 10).toFixed(1)}`;
            }

            return (
              <motion.div key={r.battery_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.05 }}
                className="rounded-2xl p-5 text-center"
                style={{ background: `${color}08`, border: `1px solid ${color}20` }}
                data-testid={`public-score-${r.battery_id}`}>
                <p className="text-[9px] font-bold uppercase tracking-wider mb-2" style={{ color: '#9CA3AF' }}>
                  {name.split('(')[0].trim().split(' ').slice(0, 2).join(' ')}
                </p>
                <p className="text-2xl font-black" style={{ color }}>{scoreLabel || 'N/A'}</p>
                {scoreLabel && !isNaN(scoreLabel) && (
                  <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>/10</p>
                )}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Radar Chart */}
        {radarData.length >= 3 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="rounded-2xl p-6"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
            data-testid="public-radar">
            <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: '#8B5CF6' }}>
              Profile Snapshot
            </p>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke="rgba(255,255,255,0.08)" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: '#6B7280', fontSize: 9 }} />
                  <Radar name="Score" dataKey="score" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.2} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="text-center rounded-2xl p-8"
          style={{ background: 'linear-gradient(135deg, rgba(13,148,136,0.1), rgba(13,148,136,0.02))', border: '1px solid rgba(13,148,136,0.15)' }}
          data-testid="public-cta-section">
          <BarChart3 className="w-8 h-8 mx-auto mb-3" style={{ color: '#0D9488' }} />
          <h3 className="text-lg font-bold mb-1" style={{ color: '#F9FAFB' }}>Discover Your Career Intelligence</h3>
          <p className="text-xs mb-5 max-w-md mx-auto" style={{ color: '#9CA3AF' }}>
            Take the same 7-battery assessment suite to uncover your strengths, risks, and ideal career path.
          </p>
          <a href="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold"
            style={{ background: '#0D9488', color: '#fff' }}>
            Start Your Free Assessment <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Footer */}
        <div className="text-center py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <img src="/kairera-logo.png" alt="Kairera" className="mx-auto" style={{ height: 16, opacity: 0.4 }} />
          <p className="text-[10px] mt-2" style={{ color: '#374151' }}>Career Intelligence Platform</p>
        </div>
      </div>
    </div>
  );
}
